import { HasId } from "app/shared/model/properties.model";
import { GeneralParameters } from "app/shared/model/request-parameter.model";
import { BehaviorSubject, forkJoin, MonoTypeOperatorFunction, Observable, of, Subscriber } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { Utils } from "../../core/utils";
import { HALPageDetails, ObservableSource } from "../model/app.model";
import { PagedArray } from "app/shared/model/app.model";

export class EntityPager<T extends HasId, P extends GeneralParameters = GeneralParameters> {
	
	private currentItemSubject: BehaviorSubject<T | null>;
	currentItem$: Observable<T | null>;
	
	private nextItem?: T;
	private previousItem?: T;
	
	get currentItem() {
		return this.currentItemSubject.value;
	}

	get totalPages() {
		return this.pageDetails?.totalPages ?? -1;
	}

	get totalElements() {
		return this.pageDetails?.totalElements ?? -1;
	}

	get currentIndex() {
		return this.pageDetails?.number ?? -1;
	}

	get outOfRange() {
		return this.currentIndex > this.totalElements || this.currentIndex < 0;
	}
	
	get hasNoList() {
		return !this.pageDetails;
	}

	private _loadingDirection: EntityPagerDirection | null = null;
	get loadingDirection() {
		return this._loadingDirection;
	}

	private _isLoading = false;
	get isLoading() {
		return this._isLoading || this._loadingDirection != null;
	}

	private readonly listProvider: () => Observable<PagedArray<T>>;
	private readonly entryGetter: EntityPagerSupplier<T, P>;
	private pageDetails?: HALPageDetails;

	constructor(
		options: EntityPagerOptions<T, P>
	) {
		const startingVal = 'startingItem' in options ? options.startingItem : null;
		this.currentItemSubject = new BehaviorSubject<T | null>(startingVal);
		this.currentItem$ = this.currentItemSubject.asObservable();
		this.entryGetter = options.entryGetter;
		this.listProvider = options.listProvider;

		if (startingVal === null && 'index' in options) {
			this.updateWithIndex(options.index).subscribe();
		} 
		else if (startingVal) {
			this.pageDetails = Utils.deepCopy(startingVal.page);
		}
	}

	getNextItem<D extends EntityPagerDirection>(direction: D): Observable<EntityPagerUpdate<T>> {
		this._loadingDirection = direction;
		
		const nextEntity = direction === 'FORWARD' ? this.nextItem : this.previousItem;
		return this.fetchIndex(this.currentIndex + ((direction === 'FORWARD') ? 1 : -1 )).pipe(
			this.doWhileLoading(),
			switchMap(result => {
				if (result.current && (!nextEntity || HasId.compare(nextEntity, result.current)))
					return of(result);
				
				return this.resolveEntityNotFound(nextEntity);
			}),
			tap((update) => this.update(update)),
			tap(() => this._loadingDirection = null)
		);
	}

	hasNext(direction: EntityPagerDirection) {
		return !!(direction === 'FORWARD' ? this.nextItem : this.previousItem);
	}
	
	private update(update: EntityPagerUpdate<T>) {
		this.nextItem = update.next;
		this.previousItem = update.previous;
		this.pageDetails = update.page;
		this.currentItemSubject.next(update.current ?? null);
	}
	
	private updateWithIndex(index: number) {
		return this.fetchIndex(index).pipe(
			this.doWhileLoading(),
			switchMap((update) => {
				return (update.current) ? of(update) : this.resolveEntityNotFound();
			}),
			tap((update) => this.update(update))
		);
	}

	private fetchIndex(index: number): Observable<EntityPagerUpdate<T>> {
		index = Utils.let(parseInt(index as any), (index) => (index < 0 || isNaN(index)) ? 0 : index);
		
		const fetchAtIndex = (index: number) => this.entryGetter({ size: 1, page: index } as P);
		
		const toFetch: ObservableSource<{ current?: PagedEntry<T>, next?: PagedEntry<T>, previous?: PagedEntry<T>}> = {
			current: fetchAtIndex(index),
			next: fetchAtIndex(index + 1)
		};
		
		if (index > 0) {
			toFetch.previous = fetchAtIndex(index - 1);
		}
		
		return forkJoin(toFetch)
			.pipe(
				map(({ current, next, previous }: { current?: PagedEntry<T>, next?: PagedEntry<T>, previous?: PagedEntry<T>}) => {
					return {
						current: current,
						next: next,
						previous: previous,
						page: current?.page
					}
				})
			)
	}
	
	private resolveEntityNotFound(locateEntity?: T): Observable<EntityPagerUpdate<T>> {
		const self = this;
		return (locateEntity ? this.getList() : of(Array<T>()))
			.pipe(
				this.doWhileLoading(),
				switchMap((result) => {
					if (result.length === 0) {
						// Indicate an empty list
						return of<EntityPagerUpdate<T>>({});
					}
					
					const foundIdx = !locateEntity ? -1 : result.findIndex(r => HasId.compare(r, locateEntity));
					if (foundIdx >= 0) {
						return this.fetchIndex(foundIdx);
					}
					else if (this.currentIndex >= 0) {
						return this.fetchIndex(this.currentIndex).pipe(
							switchMap(e => {
								if (e.current) return of(e)
								return getFirst();
							})
						);
					}
					
					return getFirst();
					
					function getFirst() {
						return self.fetchIndex(0)
					}
				})
			)
	}
	
	private getList(): Observable<PagedArray<T>> {
		return this.listProvider();
	}
	
	private currentLoader?: Subscriber<any>;
	private doWhileLoading<T>(): MonoTypeOperatorFunction<T> {
		return (source$) => {
			const self = this;
			return source$.lift(function(this: Subscriber<any>, liftedSource: Observable<T>) {
				try {
					self.currentLoader = this;
					self._isLoading = true;
					liftedSource.subscribe(this);
				}
				finally {
					this.add(() => {
						if (self.currentLoader === this) self._isLoading = false
					});
				}
			});
		}
	}
}

export type EntityPagerDirection = 'FORWARD' | 'BACKWARD';
export type EntityPagerUpdate<T extends HasId> = { current?: T, previous?: T, next?: T, page?: HALPageDetails };
export type EntityPagerSupplier<T extends HasId, P> = (parameters: EntityPagerSupplierParameters<P>) => Observable<PagedEntry<T> | undefined>;
type EntityPagerSupplierParameters<P extends GeneralParameters> = Omit<P, 'size' | 'page'> & Readonly<Pick<P, 'size' | 'page'>>;
interface EntityPagerOptionKeys<T extends HasId, P> {
	index: number
	startingItem: PagedEntry<T>,
	entryGetter: EntityPagerSupplier<T, P>,
	listProvider: () => Observable<PagedArray<T>>
}
type EntityPagerOptions<T extends HasId, P extends GeneralParameters> = Pick<EntityPagerOptionKeys<T, P>, 'listProvider'> & (
	(Pick<EntityPagerOptionKeys<T, P>, 'startingItem' | 'entryGetter'>) |
	(Pick<EntityPagerOptionKeys<T, P>, 'index' | 'entryGetter'>)
);

export type PagedEntry<T> = T & { page: HALPageDetails };

export module PagedEntry {
	export function from<T>(src: PagedArray<T>, index: number): PagedEntry<T> | undefined;
	export function from<T>(item: T, page: HALPageDetails): PagedEntry<T>;
	export function from<T>(item: T | PagedArray<T>, page: number | HALPageDetails): PagedEntry<T> | undefined {
		if (PagedArray.isPagedArray(item) && typeof page === 'number') {
			const _item = item[page];
			if (!_item) return undefined;
			return create(_item, item.page);
		}
		else if (!Array.isArray(item) && typeof page !== 'number') {
			return create(item, page);
		}
		return undefined;
		
		function create(item: T, page: HALPageDetails) {
			return {
				...Utils.deepCopy(item),
				page: { ...Utils.deepCopy(page) }
			}
		}
	}
}